import {
	getPageViewMetadata,
	type PageViewMetadata,
} from "@repo/tracking/page-view/lib/page-view-metadata";
import {getSession} from "@/actions/GetSession";
import type {DiscoveryEventKey} from "@/app/actions/trackDiscoveryEventUtils";

// Unprefixed to match the funnel event_key convention (site is identified via
// the `app` property instead — see the event spec's naming discussion).
const ROUTE_EVENT_KEYS: {pattern: RegExp; eventKey: string}[] = [
	{pattern: /^\/$/, eventKey: "homepage_viewed"},
	{pattern: /^\/program\/[^/]+\/?$/, eventKey: "program_page_viewed"},
	{pattern: /^\/course\/[^/]+\/?$/, eventKey: "course_page_viewed"},
	{pattern: /^\/catalog\/?$/, eventKey: "catalog_page_viewed"},
];

// Fallback for Sanity-driven discoveryPage routes (app/[slug]/page.tsx) and
// any other Discovery route not covered above.
const DEFAULT_EVENT_KEY = "discovery_page_viewed";

export function getDiscoveryEventKey(pathname: string): string {
	const match = ROUTE_EVENT_KEYS.find(({pattern}) => pattern.test(pathname));
	return match?.eventKey ?? DEFAULT_EVENT_KEY;
}

// Slug-capturing counterparts of the patterns above, scoped to only the
// three "viewed" events actually sent through `trackDiscoveryEvent` today —
// deliberately excludes `catalog_page_viewed`/`discovery_page_viewed` (the
// fallback keys `getDiscoveryEventKey` also produces), since neither is a
// registered `DiscoveryEventKey` and neither has ever been fired.
const PAGE_VIEW_EVENT_PATTERNS: {
	pattern: RegExp;
	eventKey: DiscoveryEventKey;
	buildProperties: (
		match: RegExpMatchArray,
		pathname: string
	) => Record<string, unknown>;
}[] = [
	{
		pattern: /^\/$/,
		eventKey: "homepage_viewed",
		buildProperties: () => ({current_url_path: "/"}),
	},
	{
		pattern: /^\/program\/([^/]+)\/?$/,
		eventKey: "program_page_viewed",
		buildProperties: (match, pathname) => ({
			program_slug: match[1],
			current_url_path: pathname,
		}),
	},
	{
		pattern: /^\/course\/([^/]+)\/?$/,
		eventKey: "course_page_viewed",
		buildProperties: (match, pathname) => ({
			course_slug: match[1],
			current_url_path: pathname,
		}),
	},
];

/**
 * Maps a real, client-committed pathname to the Discovery "viewed" event it
 * represents, or `null` for routes that don't fire one (catalog, CMS-driven
 * slugs, etc). Used to fire view events from `DiscoveryPageViewMetadataRegistrar`
 * on mount/navigation — client-side, rather than from the dynamic Server
 * Component render body — since that's the only signal unaffected by
 * Next.js prefetching (Segment Cache's `Full` strategy prefetches a route's
 * dynamic content ahead of a click, indistinguishable server-side from a
 * real navigation, and would double-fire a render-time call).
 */
export function getDiscoveryPageViewEvent(
	pathname: string
): {eventKey: DiscoveryEventKey; properties: Record<string, unknown>} | null {
	for (const {pattern, eventKey, buildProperties} of PAGE_VIEW_EVENT_PATTERNS) {
		const match = pattern.exec(pathname);
		if (match) {
			return {eventKey, properties: buildProperties(match, pathname)};
		}
	}
	return null;
}

async function getDiscoveryPlatformUserId(): Promise<string | null> {
	const session = await getSession();
	return session?.userId ?? null;
}

export async function getDiscoveryPageViewMetadata(
	pathname: string
): Promise<PageViewMetadata> {
	return getPageViewMetadata({
		pathname,
		resolveEventKey: getDiscoveryEventKey,
		getPlatformUserId: getDiscoveryPlatformUserId,
	});
}
